<template>
    <div class="converter-option">
        <div ref="convertedOptionRef"
             class="converter-option__info converter-option__info--interactive"
             @click.stop="handleCurrencyClick" >
            <img loading="lazy"
                 class="converter-option__logo"
                 :src="logo"
                 :alt="`${title} logo`"
                 :title="`${title} logo`">
            <div class="converter-option__text">
                <div class="converter-option__ticker">
                    {{ ticker }}
                </div>
                <div class="converter-option__title">
                    {{ title }}
                </div>
            </div>
            <i class="fa-solid fa-chevron-down converter-option__arrow" />
        </div>
        <input ref="inputRef"
               v-model="value"
               class="converter-option__input"
               min="0"
               placeholder="0"
               type="text"
               @input="handleInput">
    </div>
</template>

<script setup lang="ts">
    import { onMounted, ref } from 'vue';
    import { useConverterModalStore } from '@/js/store/currency-converter/ConverterModalStore';

    interface Props {
        title: string,
        ticker: string,
        logo: string,
        inputWidth?: string,
        emitEvent?: boolean,
        showOnlyCrypto?: boolean;
    }

    const props = withDefaults(defineProps<Props>(), {
        inputWidth: 'auto',
        showOnlyCrypto: false,
    });

    const converterModalStore = useConverterModalStore();

    const value = defineModel<number | string>();
    // eslint-disable-next-line no-spaced-func,func-call-spacing
    const emit = defineEmits<{
        (event: 'convertedOptionRef', value: HTMLElement | null): void,
        (event: 'inputRef', value: HTMLElement | null): void
    }>();

    const convertedOptionRef = ref<HTMLElement | null>(null);
    const inputRef = ref<HTMLElement | null>(null);

    // Handles the input event for an input field, allowing only numeric values and a single decimal point.
    const handleInput = (event) => {
        let input = event.target.value;

        input = input.replace(/[^0-9.]/g, '');

        const parts = input.split('.');
        if (parts.length > 2) {
            input = `${parts[0]}.${parts.slice(1).join('')}`;
        }

        if (input.startsWith('0') && input[1] !== '.' && input.length > 1) {
            input = input.replace(/^0+/, '');
        }

        event.target.value = input;
        value.value = input;
    };

    const handleCurrencyClick = () => {
        converterModalStore.setConverterCurrencyModal(true, props.showOnlyCrypto);
    };

    onMounted(() => {
        if (props.emitEvent && convertedOptionRef.value && inputRef.value) {
            const optionElement = convertedOptionRef.value;
            const inputElement = inputRef.value;

            if (optionElement && inputElement) {
                emit('convertedOptionRef', optionElement as HTMLElement);
                emit('inputRef', inputElement as HTMLElement);
            }
        }
    });
</script>

<style scoped lang="scss">
@use "@/sass/partials/variables" as legacyVariables;
@use "@/sass/design-system/variables" as variables;
@use "@/sass/partials/mixins" as mixins;

.converter-option {
    display: flex;
    overflow: hidden;
    width: 100%;
    max-width: 100%;
    justify-content: space-between;
    padding: 24px 12px;
    border-radius: 16px;
    gap: 8px;
    text-align: left;

    &__info {
        display: flex;
        align-items: center;
        padding: 4px 0;

        &--interactive {
            padding: 6px;
            border-radius: 8px;
            cursor: pointer;
            transition: background-color 200ms ease-in-out;

            &:hover {
                background-color: rgba(legacyVariables.$border, 0.3);
            }
        }
    }

    &__input {
        display: flex;
        min-width: 0;
        max-width: v-bind(inputWidth);
        flex: 1;
        padding: 0 8px;
        border: 1px solid legacyVariables.$medium-grey;
        border-radius: 8px;
        font-weight: 700;
        text-align: right;

        &::-webkit-inner-spin-button,
        &::-webkit-outer-spin-button {
            margin: 0;
            -webkit-appearance: none;
        }

        &[type="number"] {
            -moz-appearance: textfield;
        }

        &:focus {
            outline: none;
        }
    }

    &__arrow {
        margin-left: 8px;
        color: variables.$baseBlack;
        font-size: 12px;
    }

    &__logo {
        @include mixins.round-logo(legacyVariables.$light, legacyVariables.$royal-midnight);

        width: 32px;
    }

    &__text {
        margin-left: 8px;
    }

    &__ticker {
        @include mixins.one-line-clamp;
        max-width: 100px;
        color: legacyVariables.$pill-color;
        font-size: 11px;
    }

    &__title {
        @include mixins.one-line-clamp;
        max-width: 100px;
        margin-bottom: 0;
        color: variables.$baseBlack;
        font-size: 14px;
        font-weight: 600;
        line-height: 18px;
    }
}

.dark-theme {
    .converter-option {
        &__input {
            color: variables.$baseWhite;
        }

        &__info {
            &--interactive {
                &:hover {
                    background-color: rgba(variables.$brand800, 0.5);
                }
            }
        }

        &__ticker,
        &__title {
            color: variables.$baseWhite;
        }

        &__arrow {
            color: legacyVariables.$purple;
        }
    }
}
</style>
